import React from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { FormField } from '../../../../../../../../../global/form/form-field/form-field';
import { OEToggle } from '../../../../../../../../../global/oe-toggle/oe-toggle';
import Text from '../../../../../../../../../global/text/text';
import { UpcomingSessionsTypeOptions } from '../../../../../../../../../../api/custom-pages.dao';
import { EnablementStatus } from '../../../../../../../../../../constants/common.constants';
import { DefaultPageViewModel } from '../../../default-page.viewmodel';
import { useUpcomingSessions } from './upcoming-sessions.hook';

type IProps = {
    pageVm: DefaultPageViewModel;
    sessionTypeOptions: UpcomingSessionsTypeOptions[];
};

export const UpcomingSessionsSection = observer(({ pageVm, sessionTypeOptions }: IProps): JSX.Element => {
    const {
        upcomingSessionsEnabled,
        upcomingSessionsType,
        upcomingSessionsLabelField,
        upcomingSessionsLabel,
        replaysSessionLabelField,
        replaysSessionLabel,
        replaysPreRegistrationEnabled,
        handleToggleUpcomingSessions,
        handleUpcomingSessionsTypeChange,
        handleUpcomingSessionsLabelChange,
        handleUpcomingSessionsLabelBlur,
        handleReplaysSessionLabelChange,
        handleReplaysSessionLabelBlur,
        handleTogglePreRegistration,
    } = useUpcomingSessions(pageVm);

    return (
        <>
            <FormField>
                <div className='website-custom__checkbox-label m-bottom-0px'>
                    <OEToggle
                        isSelected={upcomingSessionsEnabled}
                        onClick={handleToggleUpcomingSessions}
                        dataTestId='upcoming-sessions-toggle'
                    />
                    <Text
                        message='Upcoming sessions are {suffix}'
                        id='oe.customPage.enableUpcomingSessions'
                        description='Label text'
                        values={{
                            suffix: upcomingSessionsEnabled ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                        }}
                    />
                </div>
            </FormField>
            {upcomingSessionsEnabled && (
                <>
                    <FormField>
                        <select
                            className='oe-form-field__control'
                            value={upcomingSessionsType.value as string}
                            onChange={handleUpcomingSessionsTypeChange}
                            data-test-id='replays-upcoming-sessions-type-select'
                        >
                            {sessionTypeOptions.map(value => (
                                <option key={value} value={value}>{value}</option>
                            ))}
                        </select>
                    </FormField>
                    <FormField label={upcomingSessionsLabel}>
                        <div className='flex'>
                            <input
                                className='oe-form-field__control'
                                value={upcomingSessionsLabelField.value as string || ''}
                                type='text'
                                onChange={handleUpcomingSessionsLabelChange}
                                onBlur={handleUpcomingSessionsLabelBlur}
                                data-test-id='upcoming-sessions-label-input'
                            />
                        </div>
                        <span className='oe-form-field__error'>
                            {!upcomingSessionsLabelField.valid && upcomingSessionsLabelField.touched && (
                                <FormattedMessage
                                    values={{ fieldName: upcomingSessionsLabel }}
                                    {...upcomingSessionsLabelField.validationMessage}
                                />
                            )}
                        </span>
                    </FormField>
                    <FormField label={replaysSessionLabel}>
                        <div className='flex'>
                            <input
                                className='oe-form-field__control'
                                value={replaysSessionLabelField.value as string || ''}
                                type='text'
                                onChange={handleReplaysSessionLabelChange}
                                onBlur={handleReplaysSessionLabelBlur}
                                data-test-id='replays-session-label-input'
                            />
                        </div>
                        <span className='oe-form-field__error'>
                            {!replaysSessionLabelField.valid && replaysSessionLabelField.touched && (
                                <FormattedMessage
                                    values={{ fieldName: replaysSessionLabel }}
                                    {...replaysSessionLabelField.validationMessage}
                                />
                            )}
                        </span>
                    </FormField>
                    <FormField>
                        <div className='website-custom__checkbox-label m-top-15px m-bottom-0px'>
                            <OEToggle
                                isSelected={replaysPreRegistrationEnabled}
                                onClick={handleTogglePreRegistration}
                                dataTestId='replays-pre-registration-toggle'
                            />
                            {replaysPreRegistrationEnabled ? (
                                <Text
                                    message='Enforced pre-registration before attendees can join sessions is enabled'
                                    id='oe.defaultPage.joinSessionsWithPreregistering'
                                    description='Label text'
                                />
                            ) : (
                                <Text
                                    message='Enforced pre-registration before attendees can join sessions is disabled'
                                    id='oe.defaultPage.joinSessionsWithoutPreregistering'
                                    description='Label text'
                                />
                            )}
                        </div>
                    </FormField>
                </>
            )}
        </>
    );
});